import { useId, useRef } from 'react'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Input } from './Input.js'
import { Button } from './Button.js'

export interface BuiltOption {
  /** Stable for the life of the question — answers are stored against it. */
  id: string
  label: string
}

export interface OptionsBuilderProps {
  value: BuiltOption[]
  onValueChange: (options: BuiltOption[]) => void
  /** Which are right. Empty where the question has no right answer. */
  correctIds?: string[]
  onCorrectChange?: (ids: string[]) => void
  /** `single` allows one right answer, `multi` allows several, `none` hides it. */
  correctness?: 'single' | 'multi' | 'none'
  /**
   * How many options a question may carry. Board 6.13: *"Two options minimum,
   * **six maximum**."* The default was ten, which is a number nobody chose —
   * and the composer counted "2 of 10" at a host writing a question to be
   * answered on a phone while somebody is still talking.
   */
  max?: number
  min?: number
  disabled?: boolean
  className?: string
}

/** Short, readable, and never reused within one question. */
const newId = () => Math.random().toString(36).slice(2, 10)

/**
 * Writing the answers somebody will choose between.
 *
 * The host's composer takes `options` as `[{ id, label }]` and `correct_option_ids`
 * beside it, and nothing else on the desk authors a list of records like that —
 * `TagInput` makes bare strings with no identity, so an answer stored against the
 * third option would follow the wording if somebody edited it rather than staying
 * with the option they picked. The id is generated here and never changes, which is
 * the whole reason this is not a list of strings.
 *
 * **Two options minimum, and it says so rather than letting the push fail.** The
 * class console states the rule outright — *"a question with fewer than two options
 * cannot be pushed, and says so"* — and finding that out from the server, live, in
 * front of a room, is the moment it must not happen.
 *
 * Reordering is by button rather than by drag: the desk is used with a keyboard,
 * and a drag handle that cannot be tabbed to is an option somebody cannot move.
 * The handle is drawn because people look for it, and it is not the control.
 */
export function OptionsBuilder({
  value,
  onValueChange,
  correctIds = [],
  onCorrectChange,
  correctness = 'none',
  max = 6,
  min = 2,
  disabled,
  className,
}: OptionsBuilderProps) {
  const group = useId()
  const showCorrect = correctness !== 'none' && onCorrectChange !== undefined

  const move = (from: number, to: number) => {
    if (to < 0 || to >= value.length) return
    const next = [...value]
    const [row] = next.splice(from, 1)
    next.splice(to, 0, row!)
    onValueChange(next)
  }

  /* The answers, in order, so Enter can move to the next one and so a newly
     added row gets the cursor rather than leaving it where it was. */
  const boxes = useRef<(HTMLInputElement | null)[]>([])
  const focusRow = (i: number) => requestAnimationFrame(() => boxes.current[i]?.focus())

  const add = () => {
    if (value.length >= max) return
    onValueChange([...value, { id: newId(), label: '' }])
    focusRow(value.length)
  }

  const setCorrect = (id: string, on: boolean) => {
    if (!onCorrectChange) return
    if (correctness === 'single') return onCorrectChange(on ? [id] : [])
    onCorrectChange(on ? [...correctIds, id] : correctIds.filter((x) => x !== id))
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {value.map((option, i) => (
        <div key={option.id} className="flex items-center gap-2">
          <GripVertical className="size-4 shrink-0 text-ink-muted" aria-hidden />

          {showCorrect && (
            <input
              type={correctness === 'single' ? 'radio' : 'checkbox'}
              name={correctness === 'single' ? group : undefined}
              checked={correctIds.includes(option.id)}
              disabled={disabled}
              // Named per row, because "correct" on its own read out of context
              // says nothing about which answer it belongs to.
              aria-label={`"${option.label || `Option ${i + 1}`}" is correct`}
              className="size-4 shrink-0 accent-coral-fill"
              onChange={(e) => setCorrect(option.id, e.target.checked)}
            />
          )}

          <Input
            value={option.label}
            disabled={disabled}
            maxLength={200}
            placeholder={`Option ${i + 1}`}
            aria-label={`Option ${i + 1}`}
            /**
             * Enter adds the next answer, rather than saving the question.
             *
             * Typing a list is typing a list: you finish one, you press
             * Enter, and you expect an empty box under it. Inside a `<form>`
             * the browser's answer is to submit instead, which would save a
             * half-written question the moment somebody reached for the
             * second option. On the last row it adds; on any other it moves
             * on to the one below.
             */
            ref={(el) => {
              boxes.current[i] = el
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              if (i === value.length - 1) add()
              else focusRow(i + 1)
            }}
            onChange={(e) =>
              onValueChange(
                value.map((o) => (o.id === option.id ? { ...o, label: e.target.value } : o)),
              )
            }
          />

          <div className="flex shrink-0 items-center">
            <Button
              variant="tertiary"
              size="icon-sm"
              aria-label={`Move "${option.label || `option ${i + 1}`}" up`}
              disabled={disabled || i === 0}
              onClick={() => move(i, i - 1)}
            >
              ↑
            </Button>
            <Button
              variant="tertiary"
              size="icon-sm"
              aria-label={`Move "${option.label || `option ${i + 1}`}" down`}
              disabled={disabled || i === value.length - 1}
              onClick={() => move(i, i + 1)}
            >
              ↓
            </Button>
            <Button
              variant="tertiary"
              size="icon-sm"
              aria-label={`Remove "${option.label || `option ${i + 1}`}"`}
              // Removing below the minimum is not offered, rather than offered and
              // then refused.
              disabled={disabled || value.length <= min}
              onClick={() => {
                onValueChange(value.filter((o) => o.id !== option.id))
                onCorrectChange?.(correctIds.filter((x) => x !== option.id))
              }}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between gap-3">
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Plus aria-hidden />}
          disabled={disabled || value.length >= max}
          onClick={add}
        >
          Add an option
        </Button>
        <span className="text-caption text-ink-muted" aria-live="polite">
          {value.length < min
            ? `At least ${min} options, so this cannot be pushed yet`
            : `${value.length} of ${max}`}
        </span>
      </div>
    </div>
  )
}
